import type { IColorSchemeVariant } from '@sixui/ui';
import type { ReactEventHandler } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { Button, Stack, useColorScheme } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { IAny } from '~/helpers/types';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';
import { useActivateWPPlaylistsMutation } from '~/lib/api/modules/WPPlaylists/hooks/useActivateWPPlaylistsMutation';
import { getAppSettings } from '~/lib/appSettings';

export type IWPPlaylistsProps = IContainerProps & {
  appId: string;
};

export const WPPlaylists: React.FC<IWPPlaylistsProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const appQuery = useAppQuery({ appId });
  const enableAppWPPlaylistsMutation = useActivateWPPlaylistsMutation();
  const appSettings = getAppSettings();

  const userQuery = useUserQuery({});
  const user = userQuery.data;
  const userInfo = user ? parseUserInfo(user) : undefined;
  const emailAddress = userInfo?.emailAddress;

  const app = appQuery.data;
  const primaryColor =
    appQuery.data?.settings?.press?.env?.appThemeColorPrimary ??
    appSettings.theme.appDefaultPrimaryColor;

  const handleEnablePlaylists = async (): Promise<IAny> => {
    if (!emailAddress) {
      return;
    }

    return enableAppWPPlaylistsMutation.mutateAsync({
      appId,
    });
  };

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeUrl = app?.settings?.playlistManagementUrl;

  const updateIframeTheme = useCallback(
    (colorScheme: IColorSchemeVariant) => {
      iframeRef.current?.contentWindow?.postMessage(
        {
          source: 'crowdaa',
          action: 'updateTheme',
          dataScheme: colorScheme,
          primaryColor,
        },
        '*',
      );
    },
    [primaryColor],
  );

  const colorScheme = useColorScheme();
  const handleIframeLoad: ReactEventHandler<HTMLIFrameElement> = useCallback(
    () => updateIframeTheme(colorScheme.variant),
    [updateIframeTheme, colorScheme],
  );

  useEffect(() => {
    updateIframeTheme(colorScheme.variant);
  }, [updateIframeTheme, colorScheme.variant]);

  return (
    <ContentBlock sx={sx} draft={!!iframeUrl}>
      <ContentBlock.Suspense
        first
        loading={appQuery.isLoading || userQuery.isLoading}
        error={appQuery.error ?? userQuery.error}
      >
        {iframeUrl ? (
          <ContentBlock.Iframe
            ref={iframeRef}
            src={iframeUrl}
            title={t('WPPlaylists.iframeTitle')}
            onLoad={handleIframeLoad}
          />
        ) : (
          <ContentBlock.Empty title={t('WPPlaylists.notConfigured.title')}>
            <Stack gap={3}>
              <div>
                <Button onClick={handleEnablePlaylists}>
                  {t('WPPlaylists.notConfigured.actionLabel')}
                </Button>
              </div>
              {enableAppWPPlaylistsMutation.isError ? (
                <ErrorMessage error={enableAppWPPlaylistsMutation.error} />
              ) : null}
            </Stack>
          </ContentBlock.Empty>
        )}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
